import { Children, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Dropdown from '../../receptura/Dropdown';
import ModalShell from '../../ui/ModalShell';
import { SECTION_COLOR } from '../lide';

/**
 * Stavební kameny karty zaměstnance (modul Lidé).
 *
 * Norma §1c: v detailu se **karta v kartě nekreslí** — sekce odděluje vlasová
 * linka nahoře, hodnota má popisek **nad** sebou, mřížka hodnot jde na dva
 * sloupce, na mobilu na jeden. Jedna barva — barva sekce; dřív měla každá
 * sekce jinou ikonu v jiné barvě (oranžová, fialová, zelená, modrá…).
 */

export const muted = (isDark) => (isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)');
export const linka = (isDark) => (isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)');

export function inputStyl(isDark) {
    return {
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
        borderRadius: 8, padding: '8px 10px', fontSize: 13, width: '100%', outline: 'none',
    };
}

/** Sekce karty: nadpis (uppercase) + volitelná akce vpravo, obsah pod ním. */
export function Sekce({ nazev, akce = null, children, isDark, prvni = false }) {
    return (
        <section style={{ paddingTop: prvni ? 4 : 18 }}>
            <div className="flex items-center justify-between" style={{ minHeight: 28, marginBottom: 2 }}>
                <h4 className="text-[10.5px] font-bold uppercase" style={{ color: muted(isDark), letterSpacing: '0.06em' }}>{nazev}</h4>
                {akce}
            </div>
            {children}
        </section>
    );
}

/** Textové tlačítko v nadpisu sekce (Upravit, Zobrazit…). */
export function SekceAkce({ children, onClick, isMobile }) {
    return (
        <button type="button" onClick={onClick}
            style={{
                background: 'none', border: 'none', cursor: 'pointer', color: SECTION_COLOR,
                fontSize: 12.5, fontWeight: 600, padding: isMobile ? '10px 0 10px 12px' : '2px 0',
                WebkitTapHighlightColor: 'transparent',
            }}>
            {children}
        </button>
    );
}

const prazdnyRadek = (d) => {
    const h = d?.props?.hodnota;
    return h == null || h === '';
};

/**
 * Mřížka řádků — dva sloupce na desktopu, jeden na mobilu (norma §1c).
 *
 * Se `skryjPrazdne` nevyplněné řádky nezaberou v mřížce místo, ale nezmizí:
 * sečtou se do tiché patičky pod sekcí. Šest řádků s pomlčkou vypadá jako
 * data a přitom neříká nic; jedna věta pod nimi řekne totéž.
 */
export function Radky({ children, isMobile, isDark, skryjPrazdne = false }) {
    const mrizka = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap: 28 };
    const vsechny = Children.toArray(children).filter(Boolean);
    if (!skryjPrazdne) return <div style={mrizka}>{vsechny}</div>;

    const chybi = vsechny.filter(prazdnyRadek).map(d => d.props.popis).filter(Boolean);
    return (
        <>
            <div style={mrizka}>{vsechny.filter(d => !prazdnyRadek(d))}</div>
            {chybi.length > 0 && (
                <p className="text-[12px]" style={{ color: muted(isDark), marginTop: 8 }}>
                    Nevyplněno: {chybi.join(', ').toLowerCase()}
                </p>
            )}
        </>
    );
}

/**
 * Hodnota s popiskem **nad** ní (norma §1c od 2026-09-29).
 *
 * Dřív stál popisek vlevo a hodnota vpravo u druhé hrany — na kartě přes
 * 970 px mezi nimi zbylo až 300 px prázdna a oko muselo u každého řádku
 * přeskočit. Nad sebou stojí řádek o 8 px víc, ale hodnoty jsou zarovnané
 * vlevo, takže se sekce dá přelétnout jedním sloupcem shora dolů.
 */
export function Radek({ popis, hodnota, isDark, isMobile, mono = false, problem = false, siroky = false, tucne = false }) {
    const prazdne = hodnota == null || hodnota === '';
    return (
        <div style={{
            padding: isMobile ? '8px 0 9px' : '6px 0 7px',
            borderBottom: `1px solid ${linka(isDark)}`,
            gridColumn: siroky ? '1 / -1' : undefined,
        }}>
            {/* Ne kapitálky: ty nese nadpis sekce a dvě úrovně stejného tvaru
                nad sebou splynou — „PRACOVNÍ ÚDAJE" a „TYP ÚVAZKU" by vypadaly
                jako dva nadpisy vedle sebe. */}
            <div className="text-[11px] font-medium truncate" style={{ color: 'var(--text-secondary)', lineHeight: '14px' }}>
                {popis}
            </div>
            <div className={mono ? 'font-mono' : ''}
                style={{
                    fontSize: isMobile ? 14 : 13.5, lineHeight: isMobile ? '19px' : '18px', marginTop: 1,
                    color: problem ? '#f87171' : (prazdne ? muted(isDark) : 'var(--text-primary)'),
                    fontWeight: problem || tucne ? 600 : 500, overflowWrap: 'anywhere',
                }}>
                {prazdne ? '—' : hodnota}
            </div>
        </div>
    );
}

/** Pole formuláře s popiskem. Na mobilu pod sebou (norma §1c). */
export function Pole({ label, children, siroky = false, isDark }) {
    return (
        <div style={{ gridColumn: siroky ? '1 / -1' : undefined }}>
            <label className="block text-xs mb-1" style={{ color: muted(isDark) }}>{label}</label>
            {children}
        </div>
    );
}

export function Formular({ children, isMobile }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, padding: '8px 0 4px' }}>
            {children}
        </div>
    );
}

export function Vstup({ isDark, style, ...props }) {
    return <input {...props} style={{ ...inputStyl(isDark), ...style }} />;
}

/**
 * Výběr hodnoty — vždy naše `Dropdown`, nikdy nativní `<select>`.
 * `options` = [{ value, label }]; `onChange` dostane rovnou hodnotu.
 */
export function Vyber({ value, onChange, options, placeholder = '— Vyberte —', isDark, prazdna = true }) {
    const volby = prazdna ? [{ value: '', label: placeholder }, ...options] : options;
    return (
        <Dropdown value={value ?? ''} onChange={onChange} options={volby.map(o => ({ ...o, value: o.value === null ? '' : o.value }))}
            placeholder={placeholder} isDark={isDark} accent={SECTION_COLOR} />
    );
}

export function Zaskrtavatko({ checked, onChange, label, isDark }) {
    return (
        <label className="flex items-center gap-2 cursor-pointer" style={{ minHeight: 36 }}>
            <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: SECTION_COLOR }} />
            <span className="text-sm" style={{ color: isDark ? 'rgba(255,255,255,0.72)' : 'var(--text-secondary)' }}>{label}</span>
        </label>
    );
}

/** Uložit / Zrušit pod formulářem sekce. Na mobilu na plnou šířku (norma §1c). */
export function TlacitkaFormulare({ onUlozit, onZrusit, uklada = false, isDark, isMobile, popisUlozit = 'Uložit' }) {
    return (
        <div className={isMobile ? 'flex flex-col gap-2' : 'flex gap-2'} style={{ gridColumn: '1 / -1', marginTop: 4 }}>
            <button type="button" onClick={onUlozit} disabled={uklada}
                className="rounded-lg font-semibold disabled:opacity-50"
                style={{ height: isMobile ? 44 : 34, padding: '0 16px', background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                {uklada ? 'Ukládám…' : popisUlozit}
            </button>
            <button type="button" onClick={onZrusit}
                className="rounded-lg"
                style={{
                    height: isMobile ? 44 : 34, padding: '0 16px', fontSize: 13, cursor: 'pointer',
                    background: 'transparent', color: 'var(--text-secondary)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                }}>
                Zrušit
            </button>
        </div>
    );
}

/** Tlačítko se sekundárním vzhledem (akce v hlavičce, v řádku dokumentu…). */
export function Tlacitko({ children, onClick, isDark, primarni = false, nebezpecne = false, disabled = false, vyska = 32, title, href, ...rest }) {
    const styl = {
        height: vyska, padding: '0 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer',
        whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1, textDecoration: 'none',
        background: primarni ? SECTION_COLOR : 'transparent',
        color: primarni ? '#fff' : nebezpecne ? '#f87171' : 'var(--text-secondary)',
        border: primarni ? 'none' : `1px solid ${nebezpecne ? 'rgba(239,68,68,0.3)' : (isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)')}`,
    };
    if (href) return <a href={href} target="_blank" rel="noreferrer" title={title} style={styl} {...rest}>{children}</a>;
    return <button type="button" onClick={onClick} disabled={disabled} title={title} style={styl} {...rest}>{children}</button>;
}

/**
 * Potvrzení nevratné akce — `ModalShell` místo `window.confirm` (ten na
 * mobilu vyskočí jako systémový dialog mimo aplikaci a nejde nastylovat).
 */
export function PotvrzeniModal({ nadpis, text, potvrdit = 'Smazat', onPotvrdit, onZavri, isDark }) {
    const [bezi, setBezi] = useState(false);
    return (
        <ModalShell onClose={onZavri} isDark={isDark} nadpis={nadpis} ikona={AlertTriangle} barva="#EF4444" sirka={420}
            paticka={(
                <div className="flex gap-2 justify-end">
                    <Tlacitko isDark={isDark} onClick={onZavri} vyska={40}>Zrušit</Tlacitko>
                    <button type="button" disabled={bezi}
                        onClick={async () => { setBezi(true); try { await onPotvrdit(); } finally { setBezi(false); } }}
                        className="rounded-lg font-semibold disabled:opacity-50"
                        style={{ height: 40, padding: '0 16px', background: '#EF4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                        {bezi ? 'Moment…' : potvrdit}
                    </button>
                </div>
            )}>
            <p className="text-sm" style={{ padding: 16, color: 'var(--text-secondary)' }}>{text}</p>
        </ModalShell>
    );
}

/** Hláška pod akcí (úspěch / chyba) — místo `alert()`. */
export function Hlaska({ hlaska }) {
    if (!hlaska) return null;
    const ok = hlaska.typ === 'ok';
    return (
        <p className="text-xs" style={{ color: ok ? '#34d399' : '#f87171', marginTop: 6 }}>{hlaska.text}</p>
    );
}
